import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  mapToAccountDetailsDto,
  mapToPersonalDetailsDto,
} from '../Integrations/mapper';
import { calculateAge, thunkHandler } from '../utils/utilFunctions';
import { AddressState, ApplicantsState, CommonState } from './types';

/* Async Actions */
export const getReferenceData = createAsyncThunk(
  'common/getReferenceData',
  async (_, { rejectWithValue }) => {
    return thunkHandler(
      axios({
        method: 'get',
        url: process.env.REACT_APP_API + '/application/get-reference-data',
      }),
      rejectWithValue
    );
  }
);

export const createApplication = createAsyncThunk<
  any,
  { mobileNumber: string; customerId: string }
>('common/createApplication', async (data, { getState, rejectWithValue }) => {
  const { mobileNumber, customerId } = data;
  const { common, applicants } = getState() as {
    common: CommonState;
    applicants: ApplicantsState;
  };

  return thunkHandler(
    axios({
      method: 'post',
      url: process.env.REACT_APP_API + '/application',
      data: {
        productCode: common.accountType,
        isMinorApplication: common.isMinorApplication,
        applicantNumber: applicants.applicants.length,
        campaignCode: '',
        mobileNumber: mobileNumber,
        customerId: customerId,
      },
    }),
    rejectWithValue
  );
});

export const getMemberDetails = createAsyncThunk<
  any,
  { customerId: string; password: string; applicantIndex: number }
>('applicants/getMemberDetails', async (data, { rejectWithValue }) => {
  const { customerId, password, applicantIndex } = data;

  return thunkHandler(
    (async (): Promise<any> => {
      try {
        const response = await axios({
          method: 'post',
          url:
            process.env.REACT_APP_API + '/existing-member/get-member-details',
          data: {
            customerId: customerId,
            password: password,
          },
        });

        response.data.applicantIndex = applicantIndex;
        response.data.age = calculateAge(response.data.dob);
        return response;
      } catch (error) {
        throw error;
      }
    })(),
    rejectWithValue
  );
});

export const sendSMS = createAsyncThunk(
  'applicants/sendSMS',
  async (mobileNumber: string, { rejectWithValue }) => {
    return thunkHandler(
      axios({
        method: 'post',
        url: process.env.REACT_APP_API + '/send-code',
        data: {
          mobileNumber: mobileNumber,
        },
      }),
      rejectWithValue
    );
  }
);

export const securityCodeValidation = createAsyncThunk<
  any,
  { mobileNumber: string; securityCode: string }
>('applicants/securityCodeValidation', async (data, { rejectWithValue }) => {
  const { mobileNumber, securityCode } = data;

  return thunkHandler(
    axios({
      method: 'post',
      url: process.env.REACT_APP_API + '/verify',
      data: {
        mobileNumber: mobileNumber,
        smsVerificationCode: securityCode,
      },
    }),
    rejectWithValue
  );
});

export const updatePersonalDetails = createAsyncThunk<
  any,
  { saveForLater: boolean; currentApplicantIndex: number }
>(
  'applicants/updatePersonalDetails',
  async (data, { getState, rejectWithValue }) => {
    const { saveForLater, currentApplicantIndex } = data;

    const { common, applicants } = getState() as {
      common: CommonState;
      applicants: ApplicantsState;
    };

    let isComplete = false;
    if (common.accountType === 'CC') {
      if (
        applicants.applicants[currentApplicantIndex].memberDetails
          .isExistingMember
      ) {
        isComplete = true;
      } else if (common.currentStep === 'Verify your identity') {
        isComplete = true;
      }
    } else {
      if (
        applicants.applicants[currentApplicantIndex].memberDetails
          .isExistingMember
      ) {
        isComplete = true;
      } else if (common.currentStep === 'Tax details') {
        isComplete = true;
      }
    }

    return thunkHandler(
      axios({
        method: 'post',
        url: process.env.REACT_APP_API + '/applicant/personal-details',
        data: {
          ...mapToPersonalDetailsDto(
            applicants.applicants[currentApplicantIndex],
            currentApplicantIndex,
            common,
            isComplete,
            saveForLater
          ),
        },
        params: {
          saveForLater: saveForLater,
        },
      }),
      rejectWithValue
    );
  }
);

export const updateAccountDetails = createAsyncThunk<
  any,
  { saveForLater: boolean; currentApplicantIndex: number }
>(
  'applicants/updateAccountDetails',
  async (data, { getState, rejectWithValue }) => {
    const { saveForLater, currentApplicantIndex } = data;

    const { common, applicants } = getState() as {
      common: CommonState;
      applicants: ApplicantsState;
    };

    return thunkHandler(
      axios({
        method: 'post',
        url: process.env.REACT_APP_API + '/applicant/account-details',
        data: {
          ...mapToAccountDetailsDto(
            applicants.applicants[currentApplicantIndex],
            currentApplicantIndex,
            common,
            saveForLater
          ),
        },
        params: {
          saveForLater: saveForLater,
        },
      }),
      rejectWithValue
    );
  }
);

export const saveForLaterEmail = createAsyncThunk<
  any
>(
  'applicants/saveForLaterEmail',
  async (data, { getState, rejectWithValue }) => {

    const { common, applicants } = getState() as {
      common: CommonState;
      applicants: ApplicantsState;
    };

    return thunkHandler(
      axios({
        method: 'post',
        url: process.env.REACT_APP_API + '/applicant/send-email',
        data: {
          applicantId: applicants.applicants[0].applicantId,
          applicationCode: common.referenceNumber,
        },
      }),
      rejectWithValue
    );
  }
);

export const submitApplication = createAsyncThunk(
  'applicants/submitApplication',
  async (_, { getState, rejectWithValue }) => {
    const { common } = getState() as {
      common: CommonState;
    };

    const params = new URLSearchParams({
      applicationCode: common.referenceNumber,
    });

    return thunkHandler(
      axios({
        method: 'post',
        url:
          process.env.REACT_APP_API +
          '/application/submit?' +
          params.toString(),
      }),
      rejectWithValue
    );
  }
);

export const onlineSavingsEligibilityCheck = createAsyncThunk(
  'applicants/onlineSavingsEligibilityCheck',
  async (memberNumbers: string[], { rejectWithValue }) => {
    return thunkHandler(
      axios({
        method: 'post',
        url: process.env.REACT_APP_API + '/existing-member/eligibility',
        data: memberNumbers,
      }),
      rejectWithValue
    );
  }
);

export const getForeignTaxInformation = createAsyncThunk<{}, string>(
  'applicants/getForeignTaxInformation',
  async (url, { rejectWithValue }) => {
    return thunkHandler(
      axios({
        method: 'get',
        url,
      }),
      rejectWithValue
    );
  }
);

export const getInterestRate = createAsyncThunk<
  string,
  {
    depositAmount: string;
    investmentTerm: string;
    investmentTermType: string;
    interestPaymentOption: string;
  }
>('applicants/getInterestRate', async (data, { rejectWithValue }) => {
  const {
    depositAmount,
    investmentTerm,
    investmentTermType,
    interestPaymentOption,
  } = data;

  const apiUrlInterestRate = process.env.REACT_APP_INTEREST_RATE_APIM;

  const body = ({
    productId: 'TD_termdeposit',
    balance: depositAmount,
    investmentTerm: investmentTerm,
    investmentTermType: investmentTermType,
    interestPayOption: interestPaymentOption,
  });

  return thunkHandler(
    axios({
      method: 'post',
      url: apiUrlInterestRate,
      headers: {
        'OCP-Apim-subscription-key': process.env.REACT_APP_PersonalLoanAPI_SubscriptionKey
      },
      data: body
    }),
    rejectWithValue
  );
});

export const retrieveApplication = createAsyncThunk<
  any,
  { referenceNumber: string; mobileNumber: string; securityCode: string }
>('common/retrieveApplication', async (data, { rejectWithValue }) => {
  const { referenceNumber, mobileNumber, securityCode } = data;

  return thunkHandler(
    (async (): Promise<any> => {
      try {
        const response = await axios({
          method: 'post',
          url: process.env.REACT_APP_API + '/application/retrieve-application',
          data: {
            applicationCode: referenceNumber,
            primaryApplicantMobileNumber: mobileNumber,
            smsVerificationCode: securityCode,
          },
        });

        return response;
      } catch (error) {
        throw error;
      }
    })(),
    rejectWithValue
  );
});

let addressSearchCancelTokenSource;

export const addressSearch = async (
  address: string,
  accountType: string,
  poBox?: boolean
): Promise<[]> => {
  // Cancel previous axios call
  if (typeof addressSearchCancelTokenSource !== typeof undefined) {
    addressSearchCancelTokenSource.cancel(
      'Address search request canceled due to new request.'
    );
  }

  addressSearchCancelTokenSource = axios.CancelToken.source();

  const response = await axios({
    method: 'get',
    url:
      process.env.REACT_APP_API +
      '/address/search?address=' + address +
      '&accountType=' + accountType + 
      (poBox ? '&includePoBox=true' : ''),
    cancelToken: addressSearchCancelTokenSource.token,
  }).catch((error) => {
    if (axios.isCancel(error)) {
      return { data: [] }; // Not return error when canceled
    } else {
      return error;
    }
  });

  return response.data;
};

export const formatAddress = async (moniker: string): Promise<AddressState> => {
  const response = await axios({
    method: 'get',
    url:
      process.env.REACT_APP_API +
      '/address/formatted-address?moniker=' +
      encodeURIComponent(moniker),
  });

  return response.data;
};
